<template>
  <section
    v-if="skills.length || categorizedSkills.length"
    class="skills pe-section"
  >
    <h3 v-if="displayTitle">
      {{ skillsTitle || 'Core Skills' }}
      <SectionActions
        v-show="isPowerEdit"
        class="actions"
        section="skills"
        :actions="['delete', 'add']"
      />
    </h3>

    <section class="list">
      <!-- List: Categories -->
      <div
        v-for="(category, categoryIndex) in categorizedSkills"
        class="mb-2"
        :key="category.uuid"
      >
        <!-- Category Name -->
        <span
          data-test-id="category-name"
          class="leading-[inherit] text-sm-em"
          :class="{
            'font-bold': category.name,
            'text-jsNeutral-50': !category.name,
          }"
        >
          {{ category.name || 'Category name' }}:
        </span>

        <!-- List: Category Skills -->
        <CoreSkill
          v-for="(skill, skillIndex) in category.skills"
          :key="`${categoryIndex}-${skillIndex}`"
          :section="`categorizedSkills.${categoryIndex}.skills`"
          :skill="skill"
          :index="skillIndex"
          :isEditable="isPowerEdit"
          @add="addCategorySkill(categoryIndex, skillIndex)"
          @remove="removeCategorySkill(categoryIndex, skillIndex)"
        />
      </div>

      <!-- List: General Skills -->
      <CoreSkill
        v-for="(skill, index) in skills"
        section="skills"
        :key="`core-${index}`"
        :skill="skill"
        :index="index"
        :isEditable="isPowerEdit"
        @add="addSkill"
        @remove="removeSkill"
      />
    </section>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType, toRefs } from 'vue'
import { Resume } from '@/types/resume/v1'
import { useStructuredResumeStore } from '@/store/pinia/structuredResume'
import SectionActions from './SectionActions.vue'
import CoreSkill from './CoreSkill.vue'

export default defineComponent({
  name: 'Skills',
  components: {
    SectionActions,
    CoreSkill,
  },
  props: {
    skills: {
      type: Array as PropType<Resume.Skill[]>,
      required: false,
      default: () => [],
    },
    categorizedSkills: {
      type: Array as PropType<Resume.SkillCategory[]>,
      required: false,
      default: () => [],
    },
    skillsTitle: {
      type: String as PropType<string>,
      required: false,
    },
    displayTitle: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: true,
    },
  },
  setup(props) {
    const store = useStructuredResumeStore()
    const { skills, categorizedSkills } = toRefs(props)

    function addSkill(index: number) {
      skills.value.splice(index + 1, 0, { name: 'New skill...', keywords: [] })
    }

    function addCategorySkill(categoryIndex: number, index: number) {
      categorizedSkills.value[categoryIndex].skills.splice(index + 1, 0, {
        name: 'New skill...',
        keywords: [],
      })
    }

    function removeCategorySkill(categoryIndex: number, skillIndex: number) {
      store.removeSectionItem(
        `categorizedSkills.${categoryIndex}.skills`,
        skillIndex
      )
    }

    function removeSkill(skillIndex: number) {
      store.removeSectionItem('skills', skillIndex)
    }

    return {
      isPowerEdit: store.isPowerEdit,
      addSkill,
      addCategorySkill,
      removeCategorySkill,
      removeSkill,
    }
  },
})
</script>

<style lang="postcss" scoped>
.skills {
  @apply pt-6-em;
  h3 {
    @apply leading-none text-xl-em flex items-center;
    .actions {
      @apply hidden;
    }
    &:hover {
      .actions {
        @apply flex;
      }
    }
  }
  .list {
    @apply mt-4-em @container;
  }
}
</style>
